// Home v2 — desktop left navigation. Reusable across screens: pass `active`
// (the current NavKey) and an `onNavigate(route)` handler. Renders the grouped
// Wallet / Privacy / More sections from `navConfig`.
import React, { useState } from 'react'
import { Image, Pressable, ScrollView, View } from 'react-native'

import Avatar from '@common/components/Avatar'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import usePressOnEnter from '@common/hooks/usePressOnEnter'
import useTheme from '@common/hooks/useTheme'
import { THEME_TYPES } from '@common/styles/themeConfig'
import logo from '@web/assets/images/logo.png'
import logoLight from '@web/assets/images/logoLight.png'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import AccountSelectModal from '@mobile/modules/account-select/screens/AccountSelectScreen/AccountSelectModal'

import { NavItem as NavItemType, NavKey, SIDEBAR_GROUPS } from '../navConfig'
import { LAYOUT, RADII } from '../tokens'
import usePrivateModeReason from '../usePrivateModeReason'
import useSidebarAccount from '../useSidebarAccount'
import { CaretUpDownIcon, ChevronsIcon } from './HomeIcons'
import ModeToggle from './ModeToggle'
import NewBadge from './NewBadge'

type Props = {
  active: NavKey
  mode: Mode
  onNavigate: (item: NavItemType) => void
}

// Shorten a 0x… / 0zk… address to head…tail for the compact chip.
const shortenForChip = (addr: string) =>
  addr.length > 13 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr

const NavRow = ({
  item,
  active,
  disabled,
  collapsed,
  tooltip,
  onNavigate
}: {
  item: NavItemType
  active: boolean
  /** Private-wallet entry point greyed out because the account can't go private. */
  disabled?: boolean
  /** Icon-only rail mode — hide the label, center the icon, show it as a tooltip. */
  collapsed?: boolean
  /** Hover-tooltip text shown while disabled (why the action is unavailable). */
  tooltip?: string
  onNavigate: (item: NavItemType) => void
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [hovered, setHovered] = useState(false)
  const Icon = item.icon
  const color = (active ? theme.primaryText : theme.secondaryText) as string
  // An accessibilityRole stops react-native-web acting on Enter — see the hook.
  const pressOnEnter = usePressOnEnter(() => onNavigate(item), disabled)

  const row = (
    <Pressable
      disabled={disabled}
      onPress={() => onNavigate(item)}
      {...pressOnEnter}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      // Collapsed, the row is icon-only and has no text content to name it, so
      // the label has to be supplied explicitly rather than inferred.
      accessibilityRole="link"
      accessibilityLabel={t(item.label)}
      accessibilityState={{ selected: active, disabled: !!disabled }}
      // @ts-ignore-next-line — react-native-web only (accessibilityState is a no-op there)
      aria-current={active ? 'page' : undefined}
      style={{
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 12,
        paddingVertical: 12,
        paddingHorizontal: collapsed ? 0 : 16,
        borderRadius: RADII.md,
        opacity: disabled ? 0.45 : 1,
        backgroundColor: active
          ? (theme.homeNavActiveBackground as string)
          : hovered && !disabled
            ? (theme.homeNavHoverBackground as string)
            : 'transparent'
      }}
    >
      {active && (
        <View
          style={{
            position: 'absolute',
            left: -8,
            top: 8,
            bottom: 8,
            width: 4,
            borderRadius: 4,
            backgroundColor: theme.primary as string
          }}
        />
      )}
      <Icon width={20} height={20} color={color} />
      {!collapsed && (
        <Text fontSize={14} weight={active ? 'semiBold' : 'medium'} color={color}>
          {t(item.label)}
        </Text>
      )}
      {!collapsed && item.badge && <NewBadge />}
    </Pressable>
  )

  // Anchor a global tooltip when the row is disabled (explains why) or collapsed
  // (surfaces the hidden label). A disabled/icon-only Pressable still forwards
  // pointermove to the wrapper, so the tooltip can attach there (mirrors ModeToggle).
  const tooltipContent = disabled && tooltip ? tooltip : collapsed ? t(item.label) : undefined
  if (tooltipContent) {
    return (
      <View
        // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
        dataSet={createGlobalTooltipDataSet({ id: `sidebar-${item.key}`, content: tooltipContent })}
      >
        {row}
      </View>
    )
  }

  return row
}

const Sidebar: React.FC<Props> = ({ active, mode, onNavigate }) => {
  const { t } = useTranslation()
  const { theme, themeType } = useTheme()
  const isLight = themeType === THEME_TYPES.LIGHT
  const logoSource = isLight ? logoLight : logo
  const [accountHovered, setAccountHovered] = useState(false)
  const [toggleHovered, setToggleHovered] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const collapsePressOnEnter = usePressOnEnter(() => setCollapsed((c) => !c))
  const [isAccountSelectVisible, setIsAccountSelectVisible] = useState(false)
  const {
    accountName,
    accountAddress,
    displayAddress,
    accountPfp,
    accountIsSmart,
    handleModeChange,
    ensureRailgunEngine
  } = useSidebarAccount()
  // Private-key / hardware / view-only accounts can't open a private wallet — the
  // shield/unshield/send entry points are disabled (with a tooltip) for them.
  const privateModeReason = usePrivateModeReason()
  const privateBlocked = isPrivateModeBlocked(privateModeReason)
  const blockedTooltipKey =
    PRIVATE_MODE_TOOLTIP[privateModeReason as Exclude<PrivateModeReason, 'available'>]
  const blockedTooltip = blockedTooltipKey ? t(blockedTooltipKey) : blockedTooltipKey
  // Drop public-only entries (Gas Tank) while in private mode.
  const groups = SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.publicOnly || mode === ModeEnum.PUBLIC)
  })).filter((group) => group.items.length > 0)

  return (
    <View
      style={{
        width: collapsed ? LAYOUT.sidebarCollapsedWidth : LAYOUT.sidebarWidth,
        height: '100%',
        backgroundColor: theme.homeSidebarBackground as string,
        borderRightWidth: 1,
        borderRightColor: theme.homeCardBorder as string
      }}
    >
      {/* Inner scroller: fills the fixed-width column and scrolls only
          vertically when the viewport is too short (e.g. iPhone landscape).
          flexGrow on the content keeps the bottom account block pinned
          (marginTop:auto) when there's room. */}
      <ScrollView
        // Landmark, so browse-mode users can skip the whole rail in one keystroke.
        // @ts-ignore-next-line — react-native-web only
        role="navigation"
        accessibilityLabel={t('Main')}
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingVertical: 20,
          paddingHorizontal: collapsed ? 12 : 16,
          gap: 8
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header: logo + collapse/expand toggle. In the icon-only rail the logo is
          dropped and the toggle (»») centers, becoming the fold button. */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            paddingHorizontal: collapsed ? 0 : 8,
            paddingBottom: 16
          }}
        >
          {!collapsed && (
            <Image
              source={{ uri: logoSource }}
              style={{ width: 96, height: 32, resizeMode: 'contain' }}
            />
          )}
          <Pressable
            onPress={() => setCollapsed((c) => !c)}
            {...collapsePressOnEnter}
            accessibilityRole="button"
            accessibilityLabel={collapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            accessibilityState={{ expanded: !collapsed }}
            // @ts-ignore-next-line — react-native-web only
            aria-expanded={!collapsed}
            onHoverIn={() => setToggleHovered(true)}
            onHoverOut={() => setToggleHovered(false)}
            // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
            dataSet={createGlobalTooltipDataSet({
              id: 'sidebar-collapse-toggle',
              content: collapsed ? t('Expand menu') : t('Collapse menu')
            })}
            style={{
              width: 32,
              height: 32,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: RADII.md,
              backgroundColor: toggleHovered
                ? (theme.homeNavHoverBackground as string)
                : 'transparent'
            }}
          >
            <ChevronsIcon
              direction={collapsed ? 'right' : 'left'}
              size={18}
              color={theme.secondaryText as string}
            />
          </Pressable>
        </View>

        <View style={{ gap: 16 }}>
          {groups.map((group, gi) => (
            <View key={group.label} style={{ gap: 4 }}>
              {gi > 0 && (
                <View
                  style={{
                    height: 1,
                    backgroundColor: theme.homeCardBorder as string,
                    marginBottom: 12
                  }}
                />
              )}
              {!collapsed && (
                <Text
                  fontSize={12}
                  weight="semiBold"
                  appearance="secondaryText"
                  style={{
                    paddingHorizontal: 16,
                    paddingBottom: 4,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}
                >
                  {t(group.label)}
                </Text>
              )}
              <View style={{ gap: 2 }}>
                {group.items.map((item) => {
                  // Shield/Unshield are inherently private → always disabled when
                  // blocked. Send is public in public mode; only its private-mode
                  // variant needs a private wallet, so gate it on private mode.
                  const disabled =
                    privateBlocked &&
                    !!item.privateAction &&
                    (item.key !== 'send' || mode === ModeEnum.PRIVATE)
                  return (
                    <NavRow
                      key={item.key}
                      item={item}
                      active={item.key === active}
                      disabled={disabled}
                      collapsed={collapsed}
                      tooltip={disabled ? blockedTooltip : undefined}
                      onNavigate={onNavigate}
                    />
                  )
                })}
              </View>
            </View>
          ))}
        </View>

        {/* Bottom account block — elevated card (design option 1): identity chip
          opens the account picker, mode toggle switches public/private. */}
        <View style={{ marginTop: 'auto', paddingTop: 8 }}>
          <View
            style={{
              // Collapsed rail: show just the round avatar, no card chrome.
              backgroundColor: collapsed ? 'transparent' : (theme.homeCardBackground as string),
              borderWidth: collapsed ? 0 : 1,
              borderColor: theme.homeCardBorder as string,
              borderRadius: collapsed ? 0 : RADII.lg,
              padding: collapsed ? 0 : 12,
              gap: 12
            }}
          >
            <Pressable
              onPress={() => setIsAccountSelectVisible(true)}
              onHoverIn={() => setAccountHovered(true)}
              onHoverOut={() => setAccountHovered(false)}
              // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
              dataSet={
                collapsed
                  ? createGlobalTooltipDataSet({ id: 'sidebar-account', content: accountName })
                  : undefined
              }
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 6,
                // Collapsed: hug the avatar (no negative-margin bleed, circular
                // hover tint). Expanded: extend the hit area into the card padding.
                marginHorizontal: collapsed ? 0 : -4,
                marginVertical: collapsed ? 0 : -4,
                paddingHorizontal: collapsed ? 0 : 4,
                paddingVertical: collapsed ? 0 : 4,
                borderRadius: collapsed ? 999 : RADII.md,
                backgroundColor: accountHovered
                  ? (theme.homeNavHoverBackground as string)
                  : 'transparent'
              }}
            >
              <Avatar
                pfp={accountPfp}
                address={accountAddress}
                isSmart={accountIsSmart}
                size={34}
              />
              {!collapsed && (
                <>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      fontSize={13}
                      weight="semiBold"
                      appearance="primaryText"
                      numberOfLines={1}
                    >
                      {accountName}
                    </Text>
                    <Text
                      fontSize={11}
                      weight="medium"
                      appearance="secondaryText"
                      numberOfLines={1}
                    >
                      {shortenForChip(displayAddress)}
                    </Text>
                  </View>
                  <CaretUpDownIcon size={16} color={theme.secondaryText as string} />
                </>
              )}
            </Pressable>

            {!collapsed && (
              <ModeToggle
                mode={mode}
                onModeChange={handleModeChange}
                privateModeReason={privateModeReason}
                onRetryRailgunInit={ensureRailgunEngine}
                stretch
                light={isLight}
              />
            )}
          </View>
        </View>
      </ScrollView>

      <AccountSelectModal
        isVisible={isAccountSelectVisible}
        onClose={() => setIsAccountSelectVisible(false)}
      />
    </View>
  )
}

export default React.memo(Sidebar)
